import { cn } from '@nt/ui/cn'
import {
  TableBody,
  TableCaption,
  TableFooter,
  TableRow,
  Table as UiTable,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
  TableHeader as UiTableHeader,
} from '@nt/ui/table'
import * as React from 'react'

// The table is @nt/ui's: row rules in `border-border`, 13px medium muted heads,
// no zebra, and cell padding from the density tier (`px-cell-x py-cell-y`).
// `density="compact"` sets `data-density` on the table, which tightens every
// cell through the theme's compact tier.

export type TableDensity = 'comfortable' | 'compact'
type CellAlign = 'left' | 'right' | 'center'

const alignClass: Record<CellAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}

function Table({ density = 'comfortable', ...props }: React.ComponentProps<'table'> & { density?: TableDensity }) {
  return <UiTable density={density} {...props} />
}

function TableHeader({ sticky = false, className, ...props }: React.ComponentProps<'thead'> & { sticky?: boolean }) {
  return (
    <UiTableHeader
      className={cn(
        // Chrome ignores position:sticky on thead/tr, so the declaration is
        // mirrored onto the th cells — the level every engine honors. Tables
        // sit in cards, so the pinned row is the card's white.
        sticky && 'sticky top-0 z-10 bg-card [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-card',
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ align = 'left', className, ...props }: React.ComponentProps<'th'> & { align?: CellAlign }) {
  return <UiTableHead className={cn(alignClass[align], className)} {...props} />
}

function TableCell({ align = 'left', className, ...props }: React.ComponentProps<'td'> & { align?: CellAlign }) {
  return <UiTableCell className={cn(alignClass[align], className)} {...props} />
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
